import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ProgressCard } from 'components/apply-shared/progress-card';
import { FaFileUpload } from 'react-icons/fa';
import {
  ApplyAside,
  ApplyHeader,
  ApplyContainer,
  ApplyMain,
} from 'components/apply-shared/apply-form';
import { planStatusEnum } from 'common/utils/enums';
import { toast } from 'react-toastify';
import { useGetSinglePlanQuery } from 'redux/api/ciosUsersApi/ciosUsersApi';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import PlanStepActivatePlan from 'components/plan-activate/plan-step-activate-plan';
import PlanInReview from 'components/plan-activate/plan-in-review';
import { documentStatusEnum } from 'utils/enums';

// Define status arrays outside the component to prevent re-creation on every render.
// This prevents the useEffect hook from running infinitely.
const PENDING_STATUSES = [
  planStatusEnum.CREATED,
  planStatusEnum.PENDING,
  planStatusEnum.QUOTE,
];

const ACTIVE_STATUSES = [
  // planStatusEnum.REVIEW,
  planStatusEnum.ACTIVE,
  planStatusEnum.ON_GOING_CLAIM,
  planStatusEnum.COMPLETE,
  planStatusEnum.CLOSED,
];

const COMPLETE_STATUSES = [
  planStatusEnum.REJECTED,
  planStatusEnum.CANCELLED,
  planStatusEnum.SUSPENDED,
  planStatusEnum.EXPIRED,
];

const NOT_ALLOWED_STATUSES = [
  ...ACTIVE_STATUSES,
  ...COMPLETE_STATUSES,
  ...PENDING_STATUSES,
];

const PlanActivate = () => {
  // HOOKS
  const navigate = useNavigate(); 
  const { planId } = useParams(); // Correctly extract planId from URL parameters  

  const {
    data: planData,
    isLoading: planLoading,
    error: planError,
    refetch: refetchPlan,
  } = useGetSinglePlanQuery(planId, {
    skip: !planId,
    refetchOnMountOrArgChange: true,
  });

  const {
    trackLink,
    trackPageLoad
  } = useUserTracker();

  useEffect(() => {
    trackPageLoad('PlanActivate');
  }, []);

  // LOGIC REDIRECT
  // Make sure users with active, cancelled, or expired plans cannot access the activate page
  useEffect(() => {
    if (NOT_ALLOWED_STATUSES.includes(planData?.status)) {      
      if (PENDING_STATUSES.includes(planData?.status)) {
        navigate(`/dashboard/plans/purchase/${planId}`);
      } else if (ACTIVE_STATUSES.includes(planData?.status)) {
        navigate(`/dashboard/plans/${planId}`);
      } else {
        navigate('/dashboard/plans');
      }
    }
  }, [planData, navigate, planId]);

  // SIDE EFFECTS
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // States
  const [step, setStepNumber] = useState(0);

  // Make sure that in-review plans aren't re-activated
  useEffect(() => {
    // Only attempt to set the step based on planData if planData is available
    if (planData) {
      if (planData.status === planStatusEnum.REVIEW) {
        setStepNumber(1);
      }
    }
  }, [planData]);

  return (
    <>
      <ApplyContainer>
        {/* Header */}
        <ApplyHeader
          onClick={() => {
            navigate('/dashboard/plans');
          }}
        />

        {/* Main Content */}
        <ApplyMain>
          {/* aside */}
          {/* <ApplyAside
            // Save text is different depending on whether the plan has been created, and whether the user is on the last step
            save={() => {
              navigate('/dashboard/plans?tab=activate');
            }}
            saveText={planData?.status === planStatusEnum.REVIEW
              ? 'Return to Dashboard'
              : 'Save & Activate Later'
            }
          >
            <div className={`text-sm absolute inset-[0.5rem_0.5rem_0rem_0.5rem] flex flex-row justify-between items-start gap-2 lg:relative lg:inset-0 lg:px-3 lg:py-2 lg:border lg:border-gray-200 lg:rounded-md lg:hidden`}>
              <span className='mobile:whitespace-nowrap'>
                {planData?.status === planStatusEnum.REVIEW ? 'Plan in Review' : 'Upload Documents'}
              </span>
            </div>
            <ProgressCard
              CardIcon={FaFileUpload}
              title='Upload Documents'
              step={1}
              isBlurred={false}
              isCurrent={step === 0}
              isCompleted={planData?.leaseVerification !== documentStatusEnum.NONE && planData?.idVerification !== documentStatusEnum.NONE}
              onClick={() => {
                if (planData?.status !== planStatusEnum.REVIEW) {
                  setStepNumber(0)
                }
              }}
            />
          </ApplyAside> */}
          
          <section className='h-full w-full flex flex-col items-center border border-gray-200 divide-y divide-gray-200 rounded-lg mobile:border-none mobile:rounded-none mobile:divide-gray-200'>
            {step === 0 ? (
              <PlanStepActivatePlan
                planData={planData}
                stepNumber={step}
                onBackClick={() => {
                  navigate('/dashboard/plans?tab=activate');
                  window.scrollTo(0, 0);
                }}
                onNextClick={() => {
                  refetchPlan();
                  setStepNumber(1);
                  window.scrollTo(0, 0);
                }}
                key={1}
              />
            ) : step === 1 || planData?.status === planStatusEnum.REVIEW ? (
              <PlanInReview 
                planData={planData}
                stepNumber={step}
                key={2}
              />              
            ) : (
              navigate('/dashboard/plans')
            )}
          </section>
        </ApplyMain>
      </ApplyContainer>
    </>
  );
};

export default PlanActivate;
